import React from 'react';
import styled from 'styled-components';
import { UseFormRegisterReturn } from 'react-hook-form';
import { useTheme, withThemeProvider } from '@narvar/nth-hook-theme';
import ErrorMessage from './ErrorMessage';

interface RegisteredTextAreaProps {
  id: string;
  label: string;
  className?: string;
  containerOverridesStyle?: React.CSSProperties;
  value?: string;
  defaultValue?: string;
  errorMessage?: string;
  disabled?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event?: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event?: React.FocusEvent<HTMLTextAreaElement>) => void;
  register: UseFormRegisterReturn;
  featureName?: string;
}

const Container = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

const centeredStyle: React.CSSProperties = {
  textAlign: 'center',
  paddingLeft: 0,
  paddingRight: 0,
};

const StyledTextArea = styled.textarea<{
  s?: {
    sFocus?: React.CSSProperties;
    sDefault?: React.CSSProperties;
    sError?: React.CSSProperties;
    sContainerOverrides?: React.CSSProperties;
    centered?: boolean;
    inputActive?: boolean;
  };
}>(({ s }) => ({
  boxSizing: 'border-box',
  width: '100%',
  borderStyle: 'solid',
  borderColor: '#999',
  borderWidth: '0.0625rem',
  borderRadius: '0.125rem',
  cursor: 'text',
  padding: '0.5rem 0.5rem 0.25rem 0.5rem',
  backgroundColor: 'transparent',
  fontSize: '0.875rem',
  color: '#000',
  textAlign: 'left',
  ...(s && s.sDefault),
  '&:focus': {
    borderColor: '#111',
    outlineWidth: '0',
    outline: 'none',
    ...(s && s.sFocus),
    ...(s && s.sError),
  },
  ...(s && s.centered && centeredStyle),
  ...(s && s.sError),
  ...(s && s.sContainerOverrides),
}));

const RegisteredTextarea: React.FunctionComponent<RegisteredTextAreaProps> = ({
  className = '',
  id,
  label,
  errorMessage,
  containerOverridesStyle = {},
  value,
  defaultValue,
  disabled = false,
  onChange = (data) => data,
  onFocus = () => undefined,
  onBlur = () => undefined,
  featureName,
  register,
}) => {
  const errorId = `${id}-error`;
  const hasError = Boolean(errorMessage && errorMessage.length);
  const { onBlur: rOnBlur, onChange: rOnChange, ref, name } = register;
  const [inputActive, setInputActive] = React.useState(
    Boolean(value || defaultValue),
  );

  const {
    inputtextinputsNoneDisabled: inputStyleDisabled,
    inputtextinputsNoneFocus: inputStyleFocus,
    inputtextinputsNoneNone: inputStyle,
    captiontextErrorNone: captionTextError,
  } = useTheme();

  const errorColor = (captionTextError as { color?: string } | undefined)
    ?.color;

  return (
    <>
      <Container>
        <StyledTextArea
          id={id}
          ref={ref}
          name={name}
          className={className}
          s={{
            sError:
              hasError && errorColor
                ? { border: `1px solid ${errorColor}` }
                : {},
            sFocus: inputStyleFocus,
            sDefault: disabled ? inputStyleDisabled : inputStyle,
            sContainerOverrides: containerOverridesStyle,
            inputActive,
          }}
          placeholder={label}
          defaultValue={defaultValue}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            if (onChange) onChange(e);
            setInputActive(true);

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            rOnChange(e);
          }}
          onFocus={(): void => {
            setInputActive(true);
            if (onFocus) onFocus();
          }}
          onBlur={(e) => {
            setInputActive(Boolean(value));
            if (onBlur) onBlur(e);

            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            rOnBlur(e);
          }}
        />
      </Container>
      <ErrorMessage id={errorId} featureName={featureName} show={hasError}>
        {errorMessage ?? ''}
      </ErrorMessage>
    </>
  );
};

export default withThemeProvider(RegisteredTextarea, {
  themeTokens: [
    'inputtextinputsNoneDisabled',
    'inputtextinputsNoneFocus',
    'inputtextinputsNoneNone',
    'captiontextErrorNone',
  ],
});
